// CSS
import './Library.css';
// React hooks
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Skybase GC
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { API_BASE_URL } from '../../config';
// Skybase components
import NavBarNew from '../../components/NavBarNew/NavBarNew';
import DatabaseInstance from '../DatabaseNew/DatabaseInstance/DatabaseInstance';
// External hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';     // user authe.
import { useMediaQuery } from 'react-responsive';           // responsive CSS
import Select from 'react-select';                          // for flexible Select inputs
import makeAnimated from 'react-select/animated';           // for multi-select animations
// Media assets
import CheckmarkIcon from '../../assets/StandardIcons/checkmark-icon.png';
import SearchIcon from '../../assets/StandardIcons/search-icon-transparent.png';



// =======
// Library – represents the /library Landing page.
// =======
function Library() {

    // Destructure GC
    const { availableEnginesJson, changeActiveEngineId, categoryJson, organizations } = useData();
    const { isSubscribed, checkIsSubscribed, activeUserJson } = useAuth();
    const { isLoading: isClerkLoading, isSignedIn } = useClerkAuth();
    const animatedComponents = makeAnimated();

    // initialize react vars
    const navigate = useNavigate('');
    const location = useLocation();

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1025 });
    const isPhone = useMediaQuery({ minWidth: 650 });



    // ================
    // onMount Behavior
    // ================
    // onMount --> immediately refresh Skybase Stripe subscription status
    useEffect(() => {

        // Exit: !isSignedIn
        if (!isSignedIn) { return; };

        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();
        };

        // Run the async. subscription-check
        checkSubscription();
    }, [isClerkLoading, isSignedIn]);

    // fetch() sourceOrg names for all the cards
    function SourceOrganizationName({ orgId }) {
        const [orgName, setOrgName] = useState('Loading...');

        useEffect(() => {
            if (!orgId) return;

            const url = new URL(`${API_BASE_URL}/api/v1/organizations/${orgId}`);

            fetch(url)
            .then((res) => res.json())
            .then((data) => {
                setOrgName(data.name);
            })
            .catch((err) => {
                console.error(err);
                setOrgName('Unknown Organization');
            });
        }, [orgId]);

        return <>{orgName}</>;
    }    


    // =====================
    // REAL-TIME NAME SEARCH
    // =====================
    // state var
    const [realTimeSearchQuery, setRealTimeSearchQuery] = useState("");
    // onChange ('realTimeSearchEngine') –> immediately fetches() all matching Engines in real-time
    useEffect(() => {
        
        // Edge case: Never submit empty strings
        if (realTimeSearchQuery === "" || !realTimeSearchQuery) { return; };

        // ⚠️ developer debugging
        // if (realTimeSearchQuery.length === 1) { console.log(`SKYBASE (client): Now real-time searching`); } // only display this message once (otherwise it becomes redundant)
        // console.log(`Query: ${realTimeSearchQuery}`);

        // immediately trigger JSON fetch()
        fetchRealTimeSearchEngines();
    }, [realTimeSearchQuery]);
    // fetchRealTimeSearchEngines() – async. fn. that gets all matching engines in real-time
    const [realTimeSearchEngineJson, setRealTimeSearchEngineJson] = useState([]);
    async function fetchRealTimeSearchEngines(){

        // `GET /engines/lookup/{search_query}`
        const res = await fetch(`${API_BASE_URL}/api/v1/engines/lookup/${encodeURIComponent(realTimeSearchQuery)}`);
        if (!res.ok) { throw new Error(`SKYBASE Client (/db): Unable to fetch real-time-search for query: ${realTimeSearchQuery}`)};
        
        // extract JSON --> update JSON state var (triggers re-render)
        const engine_json = await res.json();
        setRealTimeSearchEngineJson(engine_json);

        // ⚠️ developer debugging
        // if (engine_json.length > 0) {
        //     console.log(`SKYBASE (client): Found ${engine_json.length} Matching Engines`);
        //     console.log(engine_json);
        // }

    }


    // ===========
    // ORG FILTERS
    // ===========
    // state vars
    const [filteredOrgIDs, setFilteredOrgIDs] = useState(() => {
        
        // retrieve localStorage, otherwise filter again
        const saved = localStorage.getItem("filteredOrgIDs");
        return saved ? JSON.parse(saved) : organizations.map( (org_json) => org_json.id);
    })
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("filteredOrgIDs", JSON.stringify(filteredOrgIDs));
    }, [filteredOrgIDs]);
    // addOrRemoveToFilteredOrgIDs(orgID: String) – adds/removes an Organization's UUID to the filter
    const addOrRemoveToFilteredOrgIDs = (orgID) => {
        
        // remove if already added
        if (filteredOrgIDs.includes(orgID)) {
            setFilteredOrgIDs( prev => prev.filter(x => x !== orgID) );
        // otherwise, add
        } else {
            setFilteredOrgIDs( prev => [...prev, orgID]);
            console.log(`Add orgID to filter: ${orgID}`);
        };
    };
    

    // ===================
    // SEARCH PAGE FADE-IN
    // ===================
    // re-animate upon hydration
    useEffect(() => {
        
        // Select all elements with this class
        const elements = document.querySelectorAll('.SearchPage-fade-in');

        elements.forEach(el => {
            // Remove animation class
            el.classList.remove('SearchPage-fade-in');

            // Force reflow
            void el.offsetWidth;

            // Re-add animation class
            el.classList.add('SearchPage-fade-in');
        });

    }, [availableEnginesJson]); // Re-run whenever items change


    // ===============
    // CATEGORY FILTER
    // ===============
    // activeCategoryId
    const [activeCategoryId, setActiveCategoryId] = useState(() => {
        
        
        // Option<UUID> (priority #1 – only consume ONCE immediately after redirect)
        const defaultFilterId = location.state?.defaultCategoryFilterId;
        if (defaultFilterId) {
            return defaultFilterId;
        }

        // Option<UUID> (priority #2)
        const saved = localStorage.getItem("activeCategoryId");
            
        if (!saved || saved === "undefined") {
            return categoryJson[0]?.id;
        }

        try {
            return JSON.parse(saved);
        } catch {
            localStorage.removeItem("activeCategoryId");
            return categoryJson[0]?.id;
        }
    });
    // Constraint on defaultFilterId – only consume ONCE immediately after page redirect.
    useEffect(() => {
        if (location.state?.defaultCategoryFilterId) {
            navigate(location.pathname, {
                replace: true,
                state: {},
            });
        }
    }, [location, navigate]);
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("activeCategoryId", JSON.stringify(activeCategoryId));
    }, [activeCategoryId]);


    // ================
    // CATEGORY ENGINES
    // ================
    const [categoryEngines, setCategoryEngines] = useState(availableEnginesJson); // default: ALL Engines
    useEffect(() => {
        
        // filter engines by selected category
        if (realTimeSearchQuery === "") {
            setCategoryEngines(availableEnginesJson?.filter(engine_json => engine_json.category_id === activeCategoryId));
        } else {
            setCategoryEngines(realTimeSearchEngineJson?.filter(engine_json => engine_json.category_id === activeCategoryId));
        };

    }, [activeCategoryId, availableEnginesJson, realTimeSearchEngineJson, realTimeSearchQuery]);


    // ================
    // FILTERED ENGINES
    // ================
    const [filteredEngines, setFilteredEngines] = useState(null);
    // filter engines upon activeOrgIdFilter change
    useEffect(() => {
        // categoryEngines --> filteredEngines
        setFilteredEngines(categoryEngines.filter( engine_json => {
            return filteredOrgIDs.includes(engine_json.source_organization);
        }));
        
    }, [categoryEngines, filteredOrgIDs, availableEnginesJson]);


    
    // ==================
    // React Multi-select (flexible select inputs for the MOBILE organizationSELECTOR)
    // NOTE: I'm ensuring that 'filteredOrgIDs' syncs up with this component
    // ==================
    const options = organizations?.filter( org_json => org_json.category_id === activeCategoryId).map( (orgJson) => (
        { value: orgJson.id, label: orgJson.name }
    ));
    
    // Always keep 'filteredOrgIDs' (ground truth source) in sync with 'selectorOptions'
    const [selectorOptions, setSelectorOptions] = useState([]);
    useEffect(() => {

        // first, get Orgs in the activeCategoryId
        let categoryOrgs = organizations?.filter( org_json => org_json.category_id === activeCategoryId);
        
        // ..then, get only the User's selected catOrgs
        let selectedCategoryOrgs = categoryOrgs.filter( cat_org_json => filteredOrgIDs.includes(cat_org_json.id));

        // ..finally, set the 'selectorOptions' state var
        setSelectorOptions(selectedCategoryOrgs.map( (orgJson) => (
            { value: orgJson.id, label: orgJson.name }
        )));

    }, [organizations, activeCategoryId, filteredOrgIDs]);

    // handleOrgSelectorOption(arr: Object[]) --> updates the 'selectorOptions', then updates the 'filteredOrgIDs'
    const handleOrgSelectorOption = (arr) => {
        // arr: [ {value: X, label: Y}, {value: X2, label: Y2}, ... ]

        // update 'selectorOptions'
        setSelectorOptions(arr);

        // extract the newOrgFilteredIDs --> update 'filteredOrgIDs'
        const newFilteredOrgIDs = arr.map((obj) => obj.value);
        setFilteredOrgIDs(newFilteredOrgIDs);
    }
    

    // JSX Component
    return(
        <div className='LibraryPage'>
            
            {/* Aesthetic background */}
            <div className='LibraryPage-aesthetic-bg'></div>

            {/* Navigation Bar */}
            <NavBarNew/>

            {/* === Main content (desktop) === */}
            { isDesktop && 
                // === (desktop) ===
                <div className='LibraryPage-main-content'>
                    
                    {/* === Main content layout === */}
                    <div className='DatabaseNew-main-content-layout SearchPage-fade-in'>
                        
                        {/* Left content */}
                        <div className='DatabaseNew-main-content-layout-left'>

                            {/* Buffer */}
                            <div className='DatabaseNew-main-content-layout-left-buffer'></div>

                            {/* Content */}
                            <div className='DatabaseNew-main-content-layout-left-content'>
                                {/* ..org filter */}
                                <div className='DatabaseNew-main-content-layout-left-content-org-filter'>
                                    <div className='DatabaseNew-main-content-layout-left-content-org-filter--title'>Organizations</div>
                                    <div className='DatabaseNew-main-content-layout-left-content-org-filter--subtitle'>Select to view/hide databases.</div>
                                    <div className='DatabaseNew-main-content-layout-left-content-org-filter-repeater'>
                                        {organizations?.filter( org_json => org_json.category_id === activeCategoryId).map( (orgJson, index) => (
                                            <div key={orgJson.id} className='DatabaseNew-main-content-layout-left-content-org-filter-repeater-row' onClick={() => addOrRemoveToFilteredOrgIDs(orgJson.id)}>
                                                <div className={`DatabaseNew-main-content-layout-left-content-org-filter-repeater-row--checkbox ${filteredOrgIDs.includes(orgJson.id) ? 'active' : ''}`}>
                                                    {filteredOrgIDs.includes(orgJson.id) && <img src={CheckmarkIcon} className='DatabaseNew-main-content-layout-left-content-org-filter-repeater-row--checkbox-icon' alt='Click to filter by organization.'/>}
                                                </div>
                                                <div className={`DatabaseNew-main-content-layout-left-content-org-filter-repeater-row--org-name ${filteredOrgIDs.includes(orgJson.id) ? 'active' : ''}`}>{orgJson.name}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* RIGHT */}
                        <div className='DatabaseNew-main-content-layout-right'>

                            {/* Filters */}
                            <div className='DatabaseNew-main-content-layout-right-filters'>
                                
                                {/* ..selectors */}
                                <div className='DatabaseNew-main-content-layout-right-filters-selectors'>

                                    {/* Industry filter */}
                                    <select
                                        className='DatabaseNew-main-content-layout-right-filters-selectors--dropdown'
                                            id="category"
                                            value={activeCategoryId}
                                            onChange={(e) => setActiveCategoryId(e.target.value)}
                                        >
                                            {categoryJson.map( (cat_json, index) => (
                                                <option key={index} value={cat_json.id}>{cat_json.public_display_name}</option>
                                            ))}
                                    </select>
                                </div>

                                {/* ..name-search form */}
                                <div className='DatabaseNew-main-content-layout-right-filters-name-search'>
                                    <img className='DatabaseNew-main-content-layout-right-filters-name-search-input--icon' src={SearchIcon} alt='Click to find a database by name.'/>
                                    <input
                                        className='DatabaseNew-main-content-layout-right-filters-name-search-input'
                                        type="text"
                                        placeholder="Find a database by name or ID"
                                        value={realTimeSearchQuery}
                                        onChange={(e) => setRealTimeSearchQuery(e.target.value)}
                                    />
                                </div>

                            </div>

                            {/* Repeater */}
                            <div className='DatabaseNew-main-content-layout-right-repeater'>
                                { filteredEngines ?
                                    <>
                                        {filteredEngines.filter( engine_json => engine_json.category_id === activeCategoryId).map( (engine_json) => (
                                            <div className='DatabaseNew-main-content-layout-right-repeater-instance' key={engine_json.id}>
                                                <DatabaseInstance databaseJson={engine_json}/>
                                            </div>
                                        ))}
                                    </>
                                :
                                    <>
                                        {availableEnginesJson.filter( engine_json => engine_json.category_id === activeCategoryId).map( (engine_json) => (
                                            <div className='DatabaseNew-main-content-layout-right-repeater-instance' key={engine_json.id}>
                                                <DatabaseInstance databaseJson={engine_json}/>
                                            </div>
                                        ))}
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                    

                </div>
            }


            {/* === Main content layout (mobile) === */}
            { !isDesktop &&
                <div className='DatabaseNew-main-content-mobile SearchPage-fade-in'>

                    {/* Content */}
                    <div className='DatabaseNew-main-content-mobile-content'>

                        {/* Category selector */}
                        <select
                            className='DatabaseNew-main-content-mobile-category-selector'
                            id="category"
                            value={activeCategoryId}
                            onChange={(e) => setActiveCategoryId(e.target.value)}>
                                {categoryJson.map( (cat_json, index) => (
                                    <option key={index} value={cat_json.id}>{cat_json.public_display_name}</option>
                                ))}
                        </select>

                        {/* Org selector */}
                        <div className='DatabaseNew-main-content-mobile-org-selector-content'>
                            <Select
                                className='DatabaseNew-main-content-mobile-org-selector'
                                options={options}                       // 'options' = available choices
                                value={selectorOptions}                 // 'values' = currently selected choice(s)
                                isMulti={true}                          //  Enable multi-selection
                                closeMenuOnSelect={false}               //  Do NOT auto-close after selection
                                components={animatedComponents}         //  Animate the closing
                                onChange={handleOrgSelectorOption}
                            />
                        </div>

                        {/* Search input content */}
                        <div className='DatabaseNew-main-content-mobile-search-input-content'>
                            
                            <div className='DatabaseNew-main-content-layout-right-filters-name-search'>
                                <img className='DatabaseNew-main-content-layout-right-filters-name-search-input--icon' src={SearchIcon} alt='Click to find a database by name.'/>
                                <input
                                    className='DatabaseNew-main-content-layout-right-filters-name-search-input'
                                    type="text"
                                    placeholder="Find a database by name or ID"
                                    value={realTimeSearchQuery}
                                    onChange={(e) => setRealTimeSearchQuery(e.target.value)}
                                />
                            </div>

                        </div>

                        {/* Database repeater */}
                        <div className='DatabaseNew-main-content-mobile-database-repeater'>
                            { filteredEngines ?
                                <>
                                    {filteredEngines.filter( engine_json => engine_json.category_id === activeCategoryId).map( (engine_json) => (
                                        <div className='DatabaseNew-main-content-layout-right-repeater-instance' key={engine_json.id}>
                                            <DatabaseInstance
                                                databaseJson={engine_json}
                                            />
                                        </div>
                                    ))}
                                </>
                            :
                                <>
                                    {availableEnginesJson.filter( engine_json => engine_json.category_id === activeCategoryId).map( (engine_json) => (
                                        <div className='DatabaseNew-main-content-layout-right-repeater-instance' key={engine_json.id}>
                                            <DatabaseInstance
                                                databaseJson={engine_json}
                                            />
                                        </div>
                                    ))}
                                </>
                            }
                        </div>

                    </div>


                </div>
            }
        </div>
    )
};



// Export statement
export default Library;